import React, { useMemo, useState } from 'react';
import { Clock, Play, Target, PenLine, Lightbulb, FileText, Zap, ArrowRight, CheckCircle2, Settings } from 'lucide-react';
import { useSessionContext } from '../../contexts/SessionContext';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';
import WorkloadCalculatorModal from './WorkloadCalculatorModal';

/**
 * Filter timeline items to only include actionable tasks (not reminders or events)
 * Excludes: deadline_reminders, reminders, events, and items without workflow_key
 */
const getActionableTasks = (timelineItems) => {
  if (!timelineItems || !Array.isArray(timelineItems)) return [];

  return timelineItems.filter(item => {
    // Exclude deadline reminders - these are notifications, not work tasks
    if (item.source_type === 'deadline_reminder') return false;

    // Exclude other non-actionable types
    if (['reminder', 'event', 'college_deadline', 'deadline'].includes(item.source_type)) {
      return false;
    }

    // Only workflow-structured tasks are actionable
    if (!item.workflow_key) return false;

    // Exclude completed and skipped tasks (check all possible completion/skip fields)
    if (item.completion_status === 'completed' ||
        item.completion_status === 'skipped' ||
        item.completed ||
        item.status === 'completed' ||
        item.status === 'skipped') return false;

    return true;
  });
};

/**
 * Get icon for task category
 */
function getIconForCategory(category) {
  const iconMap = {
    'testing': Target,
    'applications': FileText,
    'essays': PenLine,
    'interviews': Target,
    'profile': Target,
    'research': Lightbulb
  };
  return iconMap[category] || Target;
}

/**
 * Get color for task category
 */
function getColorForCategory(category) {
  const colorMap = {
    'testing': 'azure',
    'applications': 'emerald',
    'essays': 'teal',
    'interviews': 'coral',
    'profile': 'magenta',
    'research': 'gold'
  };
  return colorMap[category] || 'slate';
}

/**
 * EnhancedSprintBlock
 *
 * Student-facing sprint launcher with enhanced V2 styling.
 * Shows the next 3 recommended tasks from the same subworkflow based on deadline intelligence.
 * Filters out deadline_reminders and other non-actionable items.
 */
const EnhancedSprintBlock = ({ timelineItems = [], colleges = [] }) => {
  const { startSession } = useSessionContext();
  const [showCustomSprint, setShowCustomSprint] = useState(false);

  // Get deadline context to prioritize urgent work
  const deadlineInfo = useMemo(() => {
    return DeadlineIntelligenceService.getCurrentPhase(colleges);
  }, [colleges]);

  // Find the next 3 tasks to work on, prioritizing:
  // 1. Same subworkflow/college
  // 2. Upcoming deadlines
  // 3. Not completed
  const sprintTasks = useMemo(() => {
    // Get only actionable tasks (excludes deadline_reminders, events, etc.)
    const actionableTasks = getActionableTasks(timelineItems);

    // Debug: Log first few tasks to check completion status
    console.log('[EnhancedSprintBlock] Actionable tasks (first 5):', actionableTasks.slice(0, 5).map(t => ({
      id: t.id,
      title: t.title,
      completion_status: t.completion_status,
      completed: t.completed,
      status: t.status,
      workflow_key: t.workflow_key
    })));

    if (actionableTasks.length === 0) return [];

    // If we have deadline info, prioritize tasks for that college
    if (deadlineInfo?.college) {
      const urgentCollegeTasks = actionableTasks.filter(task => {
        const collegeName = task.college?.name || task.college?.global_colleges?.name;
        return collegeName === deadlineInfo.college;
      });

      // If we found tasks for the urgent college, use those
      if (urgentCollegeTasks.length > 0) {
        return urgentCollegeTasks.slice(0, 3);
      }
    }

    // Otherwise, find tasks from the same workflow as the first incomplete task
    const firstTask = actionableTasks[0];
    const sameWorkflowTasks = actionableTasks.filter(
      task => task.workflow_key === firstTask.workflow_key
    );

    // Return up to 3 tasks from same workflow, or fall back to first 3 tasks
    return sameWorkflowTasks.length >= 3
      ? sameWorkflowTasks.slice(0, 3)
      : actionableTasks.slice(0, 3);
  }, [timelineItems, deadlineInfo]);

  // Calculate total estimated time
  const totalMinutes = useMemo(() => {
    return sprintTasks.reduce((sum, task) => {
      const duration = task.mvv_selected && task.mvv_time_required
        ? task.mvv_time_required
        : (task.estimated_duration || 30);
      return sum + duration;
    }, 0);
  }, [sprintTasks]);

  const handleStartSprint = () => {
    const sprintData = {
      title: 'Quick Sprint',
      tasks: sprintTasks.map(task => ({
        ...task,
        useMVV: task.mvv_selected
      })),
      estimatedMinutes: totalMinutes,
      batchType: 'QUICK_SPRINT'
    };
    startSession(sprintData);
  };

  // Show empty state if no tasks
  const isEmpty = sprintTasks.length === 0;

  return (
    <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg overflow-hidden" style={{ marginBottom: 'clamp(1.5rem, 4vw, 2rem)' }}>
      {/* Sprint Header */}
      <div className="bg-slate-900 flex items-center justify-between sprint-header" style={{
        padding: 'clamp(0.75rem, 3vw, 1rem) clamp(1rem, 4vw, 2rem)',
        flexDirection: 'column',
        gap: '0.75rem'
      }}>
        <style>{`
          @media (min-width: 640px) {
            .sprint-header {
              flex-direction: row !important;
              gap: 1rem !important;
            }
          }
        `}</style>
        <div className="flex items-center gap-3" style={{ width: '100%' }}>
          <div className="w-2 h-2 rounded-full bg-gold-400"></div>
          <h2 className="font-bold text-white" style={{ fontSize: 'clamp(0.95rem, 2.5vw, 1.125rem)' }}>Today's Critical Path</h2>
        </div>
        {!isEmpty && (
          <div className="flex items-center gap-2 text-white sprint-stats" style={{
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
            width: '100%',
            justifyContent: 'space-between'
          }}>
            <style>{`
              @media (min-width: 640px) {
                .sprint-stats {
                  width: auto !important;
                  justify-content: flex-end !important;
                  gap: 1rem !important;
                }
              }
            `}</style>
            <span className="flex items-center gap-2">
              <span className="text-stone-400">{sprintTasks.length} tasks</span>
              <span className="text-stone-600">•</span>
              <span className="text-stone-400">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</span>
            </span>
            <span className="font-medium hidden-mobile">Gets you 60% done</span>
            <style>{`
              .hidden-mobile {
                display: none;
              }
              @media (min-width: 640px) {
                .hidden-mobile {
                  display: inline !important;
                }
              }
            `}</style>
          </div>
        )}
      </div>

      {/* Sprint Tasks */}
      <div className="space-y-4" style={{ padding: 'clamp(1rem, 4vw, 2rem)' }}>
        {isEmpty ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-teal-600 mx-auto mb-3" />
            <p className="text-slate-900 font-medium mb-1">All caught up!</p>
            <p className="text-sm text-stone-600">No critical tasks for today</p>
          </div>
        ) : (
          sprintTasks.map((task, index) => {
            const Icon = getIconForCategory(task.category);
            const color = getColorForCategory(task.category);
            const duration = task.mvv_selected && task.mvv_time_required
              ? task.mvv_time_required
              : (task.estimated_duration || 30);
            const durationText = duration >= 60
              ? `${Math.floor(duration / 60)}h ${duration % 60}m`
              : `${duration} min`;

            return (
              <div
                key={task.id}
                className="bg-white border border-slate-300 rounded-lg hover:border-slate-900 transition-all group"
                style={{ padding: 'clamp(1rem, 3vw, 1.5rem)' }}
              >
                <div className="flex items-start task-card-wrapper" style={{
                  flexDirection: 'column',
                  gap: 'clamp(0.75rem, 2vw, 1rem)'
                }}>
                  <style>{`
                    @media (min-width: 640px) {
                      .task-card-wrapper {
                        flex-direction: row !important;
                        gap: 1rem !important;
                      }
                    }
                  `}</style>

                  {/* Task number + icon row */}
                  <div className="flex items-center gap-3 flex-shrink-0 icon-row" style={{
                    width: '100%',
                    justifyContent: 'space-between'
                  }}>
                    <style>{`
                      @media (min-width: 640px) {
                        .icon-row {
                          width: auto !important;
                          justify-content: flex-start !important;
                        }
                      }
                    `}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="rounded-full bg-slate-900 text-white flex items-center justify-center font-bold" style={{
                        width: 'clamp(28px, 6vw, 32px)',
                        height: 'clamp(28px, 6vw, 32px)',
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        flexShrink: 0
                      }}>
                        {index + 1}
                      </div>
                      <div className={`bg-${color}-100 rounded-lg border border-${color}-300`} style={{
                        padding: 'clamp(0.375rem, 1.5vw, 0.5rem)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Icon className={`text-${color}-700`} style={{
                          width: 'clamp(16px, 4vw, 20px)',
                          height: 'clamp(16px, 4vw, 20px)'
                        }} />
                      </div>
                    </div>
                    {/* Mobile: show time on same row */}
                    <span className="text-slate-600 font-medium flex items-center gap-1 flex-shrink-0 mobile-time" style={{
                      fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                      whiteSpace: 'nowrap'
                    }}>
                      <Clock style={{ width: '14px', height: '14px' }} />
                      {durationText}
                    </span>
                    <style>{`
                      @media (min-width: 640px) {
                        .mobile-time {
                          display: none !important;
                        }
                      }
                    `}</style>
                  </div>

                  {/* Task content */}
                  <div className="flex-1" style={{
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden'
                  }}>
                    <div className="flex items-start justify-between gap-2" style={{
                      marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)'
                    }}>
                      <h3 className="font-bold text-slate-900" style={{
                        fontSize: 'clamp(0.875rem, 2.2vw, 1rem)',
                        flex: 1,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto'
                      }}>
                        {task.friendly_title || task.title}
                      </h3>
                      {/* Desktop: show time on right */}
                      <span className="text-slate-600 font-medium flex items-center gap-1 flex-shrink-0 desktop-time" style={{
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        marginLeft: '0.75rem',
                        whiteSpace: 'nowrap'
                      }}>
                        <Clock className="w-4 h-4" />
                        {durationText}
                      </span>
                      <style>{`
                        .desktop-time {
                          display: none !important;
                        }
                        @media (min-width: 640px) {
                          .desktop-time {
                            display: flex !important;
                          }
                        }
                      `}</style>
                    </div>

                    <div className="space-y-1.5">
                      {task.rationale && (
                        <div className="flex items-start gap-2">
                          <Zap className="text-gold-600 flex-shrink-0 mt-0.5" style={{
                            width: 'clamp(14px, 3vw, 16px)',
                            height: 'clamp(14px, 3vw, 16px)'
                          }} />
                          <p className="text-slate-700" style={{
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            flex: 1
                          }}>
                            <span className="font-medium text-slate-900">Why:</span> {task.rationale}
                          </p>
                        </div>
                      )}
                      {task.college?.name && (
                        <div className="flex items-start gap-2">
                          <ArrowRight className="text-slate-400 flex-shrink-0 mt-0.5" style={{
                            width: 'clamp(14px, 3vw, 16px)',
                            height: 'clamp(14px, 3vw, 16px)'
                          }} />
                          <p className="text-slate-600" style={{
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            flex: 1
                          }}>
                            {task.college.name}
                          </p>
                        </div>
                      )}
                      {task.notes && (
                        <div className="flex items-start gap-2">
                          <ArrowRight className="text-slate-400 flex-shrink-0 mt-0.5" style={{
                            width: 'clamp(14px, 3vw, 16px)',
                            height: 'clamp(14px, 3vw, 16px)'
                          }} />
                          <p className="text-slate-600" style={{
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            flex: 1
                          }}>
                            {task.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Sprint Action */}
      {!isEmpty && (
        <div className="space-y-3" style={{ padding: '0 clamp(1rem, 4vw, 2rem) clamp(1rem, 4vw, 2rem)' }}>
          <div className="flex gap-3 sprint-buttons" style={{ flexDirection: 'column' }}>
            <style>{`
              @media (min-width: 640px) {
                .sprint-buttons {
                  flex-direction: row !important;
                }
              }
            `}</style>
            <button
              onClick={handleStartSprint}
              className="flex-1 bg-slate-900 text-white border-2 border-slate-900 rounded-lg font-bold hover:bg-slate-800 transition-all shadow-sm flex items-center justify-center gap-2"
              style={{
                padding: 'clamp(0.75rem, 3vw, 1rem) clamp(1rem, 4vw, 1.5rem)',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                minHeight: '48px'
              }}
            >
              <Play style={{ width: 'clamp(16px, 4vw, 20px)', height: 'clamp(16px, 4vw, 20px)' }} />
              Start Sprint
            </button>
            <button
              onClick={() => setShowCustomSprint(true)}
              className="bg-white text-slate-900 border-2 border-slate-900 rounded-lg font-bold hover:bg-stone-50 transition-all shadow-sm flex items-center justify-center gap-2"
              title="Customize sprint tasks"
              style={{
                padding: 'clamp(0.75rem, 3vw, 1rem) clamp(1rem, 4vw, 1.5rem)',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                minHeight: '48px',
                width: '100%'
              }}
            >
              <Settings style={{ width: 'clamp(16px, 4vw, 20px)', height: 'clamp(16px, 4vw, 20px)' }} />
              <span className="customize-text-full">Customize</span>
              <span className="customize-text-short" style={{ display: 'none' }}>Custom</span>
            </button>
            <style>{`
              @media (min-width: 640px) {
                .sprint-buttons button:last-child {
                  width: auto !important;
                }
                .customize-text-short {
                  display: none !important;
                }
              }
              @media (max-width: 380px) {
                .customize-text-full {
                  display: none !important;
                }
                .customize-text-short {
                  display: inline !important;
                }
              }
            `}</style>
          </div>

          {/* Context hint */}
          {deadlineInfo?.college && (
            <div className="text-stone-600 text-center" style={{ fontSize: 'clamp(0.6875rem, 1.8vw, 0.75rem)' }}>
              📌 Prioritizing {deadlineInfo.college} ({deadlineInfo.daysUntil} days until deadline)
            </div>
          )}
        </div>
      )}

      {/* Custom Sprint Builder Modal */}
      <WorkloadCalculatorModal
        isOpen={showCustomSprint}
        onClose={() => setShowCustomSprint(false)}
        colleges={colleges}
        tasks={timelineItems}
        deadlineInfo={deadlineInfo}
        onStartSprint={(sprintData) => {
          startSession(sprintData);
          setShowCustomSprint(false);
        }}
      />
    </div>
  );
};

export default EnhancedSprintBlock;
